/**
 * Precificação — rótulos e definições de campo do custo fiscal por SKU.
 * Os valores/fórmulas vivem no backend (costEngine.js); aqui é só o
 * vocabulário da tela. Percentuais no wire são FRAÇÃO (0.18 = 18%).
 */

export const ICMS_TYPES = ['normal', 'st_industria', 'st_substituido', 'isento_reduzido'];

export const ICMS_TYPE_LABELS = {
    normal: 'Normal',
    st_industria: 'ST — indústria',
    st_substituido: 'ST — substituído',
    isento_reduzido: 'Isento / reduzido',
};

export const ICMS_TYPE_TONES = {
    normal: 'success',
    st_industria: 'warning',
    st_substituido: 'warning',
    isento_reduzido: 'info',
};

export const ICMS_TYPE_DESCRIPTIONS = {
    normal: 'Débito e crédito comum (CST 00/20, pICMS > 0, sem ST). Crédito de ICMS = pICMS × mercadoria; crédito de PIS/COFINS; CMV líquido.',
    st_industria:
        'Compra do substituto tributário (CST 10, CFOP 5401/6401, vICMS e vICMSST). ST integral é custo na venda em SP; fora de SP só o honorário do ST é custo e o resto recupera; crédito do ICMS próprio só na parcela fora.',
    st_substituido:
        'Compra de distribuidor já substituído (CST 60, CFOP 5405, vICMS = 0; ST em vICMSSTRet). Sem crédito próprio da entrada; o que recupera fora de SP é vICMSSTRet (+ vICMSSubstituto); sem valor na nota usa o fallback percentual e marca como estimado.',
    isento_reduzido:
        'Isenção, não incidência, diferimento ou redução de base (CST 20/30/40/41/51). Sem fórmula própria ainda: calcula como normal com os valores da nota e alerta para validar com a contabilidade.',
};

export const RULE_SCOPES = ['sku', 'ncm', 'default'];
export const RULE_SCOPE_LABELS = { sku: 'SKU', ncm: 'NCM', default: 'Padrão' };

export const MATCHED_BY_LABELS = {
    sku: 'regra do SKU',
    ncm: 'regra do NCM',
    default: 'regra padrão',
    company_default: 'padrão da empresa',
};

export const MATCH_SOURCE_LABELS = {
    ean: 'casou pelo EAN',
    supplier_code: 'casou pelo cód. do fornecedor',
    sku: 'código igual ao SKU',
    manual: 'vinculado à mão',
};

export const LINK_STATUS_LABELS = {
    sem_vinculo: 'sem vínculo',
    sugerido: 'sugerido',
    confirmado: 'confirmado',
};

export const LINK_STATUS_TONES = {
    sem_vinculo: 'danger',
    sugerido: 'warning',
    confirmado: 'success',
};

export const LINK_STATUS_OPTIONS = [
    { value: '', label: 'Todas as situações' },
    { value: 'sem_vinculo', label: 'Sem vínculo' },
    { value: 'sugerido', label: 'Sugerido (a confirmar)' },
    { value: 'confirmado', label: 'Confirmado' },
];

export const ALERT_TONES = {
    SEM_NF: 'warning',
    SEM_REGRA: 'warning',
    SEM_CADASTRO: 'neutral',
    ST_ESTIMADO: 'danger',
    ST_ZERO: 'danger',
    SEM_ALIQUOTA: 'warning',
    SEM_QUANTIDADE: 'danger',
    ISENTO_REDUZIDO: 'info',
    RECUPERA_SUBSTITUTO: 'info',
    SUGESTAO_DIVERGE: 'warning',
    VINCULO_SUGERIDO: 'warning',
    SEM_VINCULO: 'danger',
};

export const SKU_FILTER_OPTIONS = [
    { value: 'all', label: 'Todos os SKUs' },
    { value: 'sem_regra', label: 'Sem regra (padrão da empresa)' },
    { value: 'sem_nf', label: 'Sem NF-e importada' },
    { value: 'st', label: 'Com ST (indústria ou substituído)' },
    { value: 'st_estimado', label: 'ST estimado (nota sem valor)' },
    { value: 'vinculo_sugerido', label: 'Vínculo com fornecedor a confirmar' },
    { value: 'com_alerta', label: 'Com alerta' },
];

/**
 * Campos de `params` (empresa e regra). `kind`: fraction (editado em %),
 * flag (booleano) ou icms_type. `group` organiza a tela.
 */
export const PARAM_FIELDS = [
    {
        key: 'pis_cofins_pct',
        label: 'PIS/COFINS não-cumulativo',
        help: 'Crédito na entrada e débito na venda (Lucro Real). Padrão 9,25%.',
        kind: 'fraction',
        group: 'fiscal',
    },
    {
        key: 'icms_frete_credit_pct',
        label: 'Crédito de ICMS sobre frete',
        help: 'Só quando a venda tem débito de ICMS. Padrão 7%.',
        kind: 'fraction',
        group: 'fiscal',
    },
    {
        key: 'credito_pis_cofins_exclui_icms',
        label: 'Excluir ICMS da base do crédito de PIS/COFINS',
        help: 'Lei 14.592/2023 — flag a confirmar com a contabilidade.',
        kind: 'flag',
        group: 'fiscal',
    },
    {
        key: 'credito_pis_cofins_sobre_tarifas',
        label: 'Crédito de PIS/COFINS sobre comissão/taxa/frete de marketplace',
        help: 'Premissa do Thiago — flag a confirmar.',
        kind: 'flag',
        group: 'fiscal',
    },
    {
        key: 'default_icms_type',
        label: 'Tratamento padrão da empresa',
        help: 'Usado quando nenhuma regra (SKU, NCM ou padrão) casa.',
        kind: 'icms_type',
        group: 'fiscal',
    },
    {
        key: 'honorario_recuperacao',
        label: 'Honorário de recuperação do ST',
        help: 'Tipos st_*: parte do ST que é custo na venda fora de SP (o resto recupera). Padrão 10%.',
        kind: 'fraction',
        group: 'st',
    },
    {
        key: 'st_fallback_pct',
        label: 'ST estimado (fallback)',
        help: 'st_substituido sem vICMSSTRet na nota: ST ≈ % × mercadoria. Padrão 10,5%.',
        kind: 'fraction',
        group: 'st',
    },
    {
        key: 'difal_fora_caixa',
        label: 'DIFAL fora de SP (linha de caixa)',
        help: 'Tipos st_*, só no simulador. Padrão 6%.',
        kind: 'fraction',
        group: 'st',
    },
    {
        key: 'mix_sp',
        label: 'Mix de vendas em SP',
        help: 'Peso do CMV de SP no CMV blend (o resto é fora de SP). Padrão 40%.',
        kind: 'fraction',
        group: 'simulador',
    },
    {
        key: 'aliq_sp',
        label: 'Alíquota interna SP',
        help: 'Simulador, tipo normal. Padrão 18%.',
        kind: 'fraction',
        group: 'simulador',
    },
    {
        key: 'aliq_inter',
        label: 'Alíquota interestadual',
        help: 'Simulador, tipo normal. Padrão 12%.',
        kind: 'fraction',
        group: 'simulador',
    },
    {
        key: 'aliq_destino',
        label: 'Alíquota no destino',
        help: 'Simulador, tipo normal (DIFAL). Padrão 18%.',
        kind: 'fraction',
        group: 'simulador',
    },
    {
        key: 'red',
        label: 'Redução de base',
        help: 'Simulador, tipo normal. Padrão 0.',
        kind: 'fraction',
        group: 'simulador',
    },
    {
        key: 'aliq_icms_difal_fora',
        label: 'ICMS fora de SP (tipos ST)',
        help: 'Simulador, tipos st_*. Padrão 19%.',
        kind: 'fraction',
        group: 'simulador',
    },
];

export const PARAM_GROUP_LABELS = {
    fiscal: 'Constantes fiscais',
    st: 'Substituição tributária',
    simulador: 'Premissas do simulador (uso futuro)',
};
